/* eslint-disable no-underscore-dangle */

import { GLOBAL } from 'saddlebag-browser';

const ACTION_TYPE = {
  WARNING: 'warning',
  OP_EVENT: 'operationalEvent',
};

/**
 * Checks the location against a list of known Skyscanner non production hostnames.
 * @param {Location} location The location as in document.location
 * @returns {boolean} If it is a prod url
 */
const isProdUrl = (location: Location): boolean =>
  location.hostname.indexOf('sandbox') === -1 &&
  location.hostname.indexOf('pre-prod') === -1 &&
  location.hostname.indexOf('.local') === -1 &&
  location.hostname.indexOf('127.0.0.1') === -1 &&
  location.hostname.indexOf('localhost') === -1 &&
  (location.port === '80' || location.port === '443' || !location.port);

/**
 * Defines the structure of the custom window object.
 */
interface CustomLoggerWindow extends Window {
  newrelic?: {
    noticeError: (error: Error, fields?: Record<string, unknown>) => void;
  };
}

export type LogErrorFields = {
  [field: string]: unknown;
};

export type LogWarnFields = {
  message: string;
  component: string;
  [field: string]: string | number;
};

export type LogOperationalEventFields = {
  eventName: string;
  [field: string]: string | number;
};

// ESLint doesn't understand the inline function JSDoc syntax, but it is part of the TS docs and required for overload syntax
// eslint-disable-next-line valid-jsdoc
/**
 * Function supports two apis for logging errors.
 * If an Error object is provided then the error will be published to _both_ New Relic(with stack trace support) and Slipstream.
 * @type {{
 * (error: Error, optionalFields?: { [field: string]: any }) => void;
 * }}
 */
const logError = (error: Error, fields?: LogErrorFields): void => {
  const _window = GLOBAL.getWindow() as CustomLoggerWindow;
  // https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/noticeerror/
  if (_window.newrelic) {
    _window.newrelic.noticeError(error, fields);
  }

  if (!isProdUrl(_window.location)) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

// ESLint doesn't understand the inline function JSDoc syntax, but it is part of the TS docs and matched logError above
// eslint-disable-next-line valid-jsdoc
/**
 * Logs a message to NewRelic via PerformanceMark.
 * @type {{
 * (fields: { [field: string]: string | number, message: string, component: string }) => void;
 * }}
 */
const logWarn = (fields: LogWarnFields): void => {
  const _window = GLOBAL.getWindow() as CustomLoggerWindow;
  if (!fields.component) {
    throw new Error('Component required');
  }

  if (_window.performance?.mark) {
    _window.performance.mark(ACTION_TYPE.WARNING, { detail: fields });
  }
};

// ESLint doesn't understand the inline function JSDoc syntax, but it is part of the TS docs and matched logError above
// eslint-disable-next-line valid-jsdoc
/**
 * Logs an event with PerformanceMark this has been added to replace the older eventTrack from saddlebag-event-tracking.
 * @type {{
 * (fields: { [field: string]:  string | number, eventName: string }) => void;
 * }}
 */
const logOperationalEvent = (fields: LogOperationalEventFields) => {
  const _window = GLOBAL.getWindow() as CustomLoggerWindow;

  if (!fields?.eventName) {
    throw Error(`logOperationalEvent: eventName must be set.`);
  }

  if (_window.performance?.mark) {
    _window.performance.mark(ACTION_TYPE.OP_EVENT, { detail: fields });
  }
};

const ERROR_LEVEL = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug',
};

export { isProdUrl, logError, logWarn, logOperationalEvent, ERROR_LEVEL };

export default {
  isProdUrl,
  logError,
  logWarn,
  logOperationalEvent,
  ERROR_LEVEL,
};
